import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type {
  ITopFiveApiV2EndpointsStaffAddStaffAddStaffRequest,
  ITopFiveApiV2EndpointsStaffAddStaffAddStaffResponse,
} from '@/api/models';
import {
  useITopFiveApiV2EndpointsStaffAddStaffAddStaffEndpoint,
  getITopFiveApiV2EndpointsStaffGetStaffListGetStaffListEndpointQueryKey,
} from '@/api/api.generated';

type AddStaffApiRequest = ITopFiveApiV2EndpointsStaffAddStaffAddStaffRequest;
type AddStaffApiResponse = ITopFiveApiV2EndpointsStaffAddStaffAddStaffResponse;

interface ApiValidationError {
  errors?: Record<string, string[]>;
}

/**
 * Parses validation errors from API response
 */
export const parseValidationErrors = (
  error: AxiosError<ApiValidationError>
): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};

  if (error.response?.data?.errors) {
    const apiErrors = error.response.data.errors;
    for (const [field, messages] of Object.entries(apiErrors)) {
      // Convert PascalCase field names to camelCase
      const camelCaseField = field.charAt(0).toLowerCase() + field.slice(1);
      fieldErrors[camelCaseField] = messages[0] || 'Invalid value';
    }
  }

  return fieldErrors;
};

/**
 * Response type for useAddStaff hook
 */
export interface UseAddStaffResult {
  /** Mutation function to add a staff member */
  addStaff: (request: AddStaffApiRequest) => Promise<AddStaffApiResponse>;
  /** Whether the mutation is in progress */
  isPending: boolean;
  /** Whether the mutation was successful */
  isSuccess: boolean;
  /** Whether there was an error */
  isError: boolean;
  /** Error object if any */
  error: AxiosError<ApiValidationError> | null;
  /** Parsed validation errors for form fields */
  validationErrors: Record<string, string>;
  /** Reset the mutation state */
  reset: () => void;
}

/**
 * Custom hook to add a new staff member
 *
 * Uses React Query mutation for API call management.
 * Automatically invalidates the staff list cache on success.
 *
 * @example
 * ```tsx
 * const { addStaff, isPending, validationErrors } = useAddStaff();
 *
 * const handleSubmit = async (data) => {
 *   try {
 *     await addStaff(data);
 *     navigate('/staff');
 *   } catch (error) {
 *     // Validation errors are available in validationErrors
 *   }
 * };
 * ```
 */
export const useAddStaff = (): UseAddStaffResult => {
  const queryClient = useQueryClient();
  const mutation = useITopFiveApiV2EndpointsStaffAddStaffAddStaffEndpoint({
    mutation: {
      onSuccess: () => {
        // Invalidate the staff list query to refetch updated data
        queryClient.invalidateQueries({
          queryKey: getITopFiveApiV2EndpointsStaffGetStaffListGetStaffListEndpointQueryKey(),
        });
      },
    },
    axios: { withCredentials: true },
  });

  const validationErrors = mutation.error
    ? parseValidationErrors(mutation.error as AxiosError<ApiValidationError>)
    : {};

  return {
    addStaff: (request: AddStaffApiRequest) =>
      mutation.mutateAsync({ data: request }).then(response => response.data),
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error as AxiosError<ApiValidationError> | null,
    validationErrors,
    reset: mutation.reset,
  };
};

export default useAddStaff;
